/**
 * Design tokens for CardV2 components.
 * Extracted from Figma designs for Books, Movies/TV, Music, and Podcasts cards.
 */

import { COLORS, FONTS } from '../../../styles/constants';

/**
 * Spacing values in pixels
 */
export const SPACING = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  /** Gap between cards in a feed */
  cardGap: 56,
  /** Horizontal padding for card container */
  cardPadding: 0,
  /** Maximum width for card container */
  cardMaxWidth: 535,
} as const;

/**
 * Border radius values in pixels
 */
export const BORDER_RADIUS = {
  sm: 10,
  md: 10,
  lg: 10,
  xl: 10,
  circle: 180,
} as const;

/**
 * Shadow/blur values for background effects
 */
export const SHADOWS = {
  /** Book card blur effect */
  bookBlur: 26.65,
  /** Movie card blur effect */
  movieBlur: 29.455,
  /** Music/Podcast blur effect */
  defaultBlur: 35,
  /** Book cover shadow */
  bookCoverShadow: '0px 6.129px 30.645px 3.871px rgba(32,31,49,0.2)',
  /** Book cover inner shadow */
  bookInnerShadow: 'inset 0px 0px 8.756px 0px rgba(42,42,48,0.25)',
} as const;

/**
 * Font families - using existing project fonts
 */
export const CARD_FONTS = {
  title: FONTS.primary, // CaslonIonic
  body: FONTS.secondary, // Yellix
} as const;

/**
 * Typography presets
 */
export const TYPOGRAPHY = {
  /** Thought/context text - Yellix SemiBold 14px */
  thought: {
    fontFamily: FONTS.secondary,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.4,
  },
  /** Music album title - Caslon Ionic 18px */
  albumTitle: {
    fontFamily: FONTS.primary,
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 1.3,
  },
  /** Podcast title - Caslon Ionic 18px */
  podcastTitle: {
    fontFamily: FONTS.primary,
    fontSize: 18,
    fontWeight: 400,
    lineHeight: 1.3,
  },
  /** Podcast subtitle - Yellix SemiBold 14px */
  podcastSubtitle: {
    fontFamily: FONTS.secondary,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.4,
  },
  /** Duration/timestamp - Yellix SemiBold 12px */
  timestamp: {
    fontFamily: FONTS.secondary,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.4,
  },
} as const;

/**
 * Card-specific colors - using existing COLORS with extensions
 */
export const CARD_COLORS = {
  /** Primary text color */
  textPrimary: COLORS.brown.dark['100'], // #181800
  /** Secondary/light text */
  textLight: COLORS.white['100'], // #FFFFFF
  /** Overlay background */
  overlayDark: 'rgba(24,24,0,0.5)',
  /** Stronger overlay */
  overlayDarker: 'rgba(24,24,0,0.7)',
  /** Book spine border */
  bookSpineBorder: '#787f8f',
} as const;

/**
 * Component-specific dimensions
 */
export const DIMENSIONS = {
  /** Avatar size in facepile */
  avatar: 57.6,
  /** Avatar overlap in facepile */
  avatarOverlap: -8,
  /** Book mockup dimensions */
  book: {
    maxHeight: 360,
    maxWidth: 310,
    spineWidth: 11.382,
  },
  /** Movie poster dimensions */
  movie: {
    containerHeight: 360,
    posterWidth: 242,
  },
  /** Music album dimensions */
  music: {
    albumSize: 310,
  },
  /** Podcast dimensions */
  podcast: {
    thumbnailSize: 46,
    videoWidth: 342,
    videoHeight: 192,
    playButtonSize: 32,
  },
  /** Footer height */
  footerHeight: 20,
  /** Action button padding */
  actionButtonPadding: 6,
  /** Action icon size */
  actionIconSize: 24,
} as const;

/**
 * Opacity values for blur effects
 */
export const OPACITY = {
  bookBlur: 0.5,
  movieBlur: 0.5,
  musicBlur: 0.6,
  podcastBlur: 0.6,
} as const;

/**
 * Z-index values for layering
 */
export const Z_INDEX = {
  blurBackground: 0,
  content: 1,
  overlay: 2,
  controls: 3,
} as const;

/**
 * Shared entrance animation tokens (loading → visible) for all card types
 */
export const CARD_ENTRANCE = {
  titleOpacity: 0,
  contentOpacity: 0,
  contentOffsetY: 48,
  contentInitialScale: 0.8,
  opacityDuration: 0.4,
  scaleDuration: 0.8,
  easing: 'cubic-bezier(0.35, 0, 0, 1.00)',
};

/**
 * Music card animation tokens
 */
export const MUSIC_ANIMATION = {
  // Disc dimensions
  discSize: 308,
  discInitialScale: 0.935, // 288/308
  centerArtSize: 128,

  // Album cover animation
  coverSlideX: -300,
  coverScaleEnd: 0.5,
  coverDuration: 0.8,
  coverEasing: 'cubic-bezier(0.1, 0, 0.65, 1.00)',

  // Disc animation
  discSettleX: 0,
  discDelay: 0.25,
  discDuration: 1.0,
  discEasing: 'cubic-bezier(0.3, 1.4, 0.5, 1)', // overshoot easing

  // Entrance animation (loading → visible) — delegates to shared CARD_ENTRANCE
  entranceTitleOpacity: CARD_ENTRANCE.titleOpacity,
  entranceCoverOpacity: CARD_ENTRANCE.contentOpacity,
  entranceCoverOffsetY: CARD_ENTRANCE.contentOffsetY,
  entranceCoverInitialScale: CARD_ENTRANCE.contentInitialScale,
  entranceOpacityDuration: CARD_ENTRANCE.opacityDuration,
  entranceScaleDuration: CARD_ENTRANCE.scaleDuration,
  entranceEasing: CARD_ENTRANCE.easing,

  // Exit animation (visible → scrolled out)
  exitCoverDuration: 0.6,
  exitDiscDuration: 0.6,
  exitDiscEasing: 'cubic-bezier(0.5, 0, 0, 1)',

  // Disc continuous rotation
  discRotationDuration: 24, // seconds for full 360°

  // Blurred background animation
  blurInitialSize: 310,
  blurInitialRadius: 10,
  blurInitialFilter: 70,
  blurExpandedScale: 1.084, // 336/310
  blurExpandedFilter: 100,
  blurDuration: 0.8,
} as const;

/**
 * Podcast card dynamic animation tokens
 */
export const PODCAST_ANIMATION = {
  // Cover animation
  coverSlideX: -60,
  coverRotation: -3,
  coverScaleEnd: 0.7,
  coverDuration: 1.0,
  coverEasing: 'cubic-bezier(0.5, 0, 0, 1)',

  // Tape reel motion (offset from center, positive = right/down)
  reelEndX: 85,
  reelEndY: 60,
  reelInitialScale: 0.7,
  reelDuration: 1.0,
  reelEasing: 'cubic-bezier(0.5, 0, 0, 1)',

  // Tape reel continuous rotation
  reelRotationDuration: 10, // seconds for full 360°

  // Blur animation
  blurInitialFilter: 70,
  blurExpandedFilter: 100,
  blurDuration: 1.0,

  // Mute button appearance
  muteButtonDelay: 0.75,
  muteButtonDuration: 0.55,
  muteButtonEasing: 'cubic-bezier(0.5, 0, 0, 1)',

  // Entrance animation — delegates to shared CARD_ENTRANCE
  entranceTitleOpacity: CARD_ENTRANCE.titleOpacity,
  entranceCoverOpacity: CARD_ENTRANCE.contentOpacity,
  entranceCoverOffsetY: CARD_ENTRANCE.contentOffsetY,
  entranceCoverInitialScale: CARD_ENTRANCE.contentInitialScale,
  entranceOpacityDuration: CARD_ENTRANCE.opacityDuration,
  entranceScaleDuration: CARD_ENTRANCE.scaleDuration,
  entranceEasing: CARD_ENTRANCE.easing,
} as const;

/**
 * Layout card design tokens (Standard, FullBleed, Immersive).
 * For non-canonical card rendering.
 */
export const LAYOUT_CARD = {
  // Content area padding
  STANDARD_CONTENT_PADDING: { top: 20, sides: 12, bottom: 12 },
  FULL_BLEED_PADDING: { top: 40, sides: 24, bottom: 24 },
  IMMERSIVE_PADDING: { top: 40, sides: 24, bottom: 40 },

  // Typography
  TITLE_FONT_SIZE: { standard: 18, fullBleed: 18, immersive: 22 },
  EYEBROW_FONT_SIZE: 12,

  // Colors
  DIVIDER_COLOR: 'rgba(255, 255, 255, 0.15)',
  OVERLAY_OPACITY: 0.6,
  SOURCE_TEXT_OPACITY: 0.6,

  // Default gradient fallback (neutral olive from Figma)
  DEFAULT_GRADIENT_FROM: '#393e27',
  DEFAULT_GRADIENT_TO: '#282c1b',

  // Dimensions
  IMMERSIVE_HEIGHT: 560,
  IMAGE_BORDER_RADIUS: 8,
  CONTENT_BORDER_RADIUS: 10,

  // Noise texture
  NOISE_OPACITY: 0.5,
} as const;
